import React, { useState, useEffect } from 'react';
import './SectionsSelector.css';
import SettingInput from './SettingInput';

function SectionsSelector({ config, selectedSections, onSectionToggle, sectionSettings, onSettingChange }) {
  const [showingSettings, setShowingSettings] = useState({});
  // Initialize with all categories from config, fallback to default
  const [activeCategories, setActiveCategories] = useState(() => {
    if (config?.categories) {
      return new Set(config.categories);
    }
    return new Set(['crypto', 'stocks', 'news']);
  });

  // Update activeCategories when config changes
  useEffect(() => {
    if (config?.categories) {
      setActiveCategories(new Set(config.categories));
    }
  }, [config]);

  const toggleSettingsView = (sectionKey, e) => {
    e.stopPropagation();
    setShowingSettings(prev => ({
      ...prev,
      [sectionKey]: !prev[sectionKey]
    }));
  };

  const toggleCategory = (category) => {
    setActiveCategories(prev => {
      const newCategories = new Set(prev);
      if (newCategories.has(category)) {
        newCategories.delete(category);
      } else {
        newCategories.add(category);
      }
      return newCategories;
    });
  };

  // Get categories from config, fallback to extracting from sections
  const allCategories = config?.categories || 
    (config?.sections ? [...new Set(Object.values(config.sections).map(section => section.category))] : []);
  
  // Filter sections based on active categories
  const filteredSections = config?.sections ? Object.keys(config.sections).filter(sectionKey => 
    activeCategories.has(config.sections[sectionKey].category)
  ) : [];

    return (
      <div className="sections-container component-card scrollable-compact">
        <div className="sections-header">
          <h2>SECTIONS ({selectedSections.length}/6)</h2>
          
          {/* Category filters */}
          <div className="category-filters">
            {allCategories.map(category => (
              <button
                key={category}
                className={`category-filter ${activeCategories.has(category) ? 'active' : ''}`}
                onClick={() => toggleCategory(category)}
              >
                {category}
              </button>
            ))}
          </div>
        </div>
      
      <div className="sections-grid">
        {config?.sections && filteredSections.map((sectionKey) => {
          const sectionData = config.sections[sectionKey];
          const isShowingSettings = showingSettings[sectionKey];
          const hasSettings = sectionData?.settings && Object.keys(sectionData.settings).length > 0;
          
          return (
            <div
              key={sectionKey}
              className={`section-card ${selectedSections.includes(sectionKey) ? 'selected' : ''}`}
              onClick={() => !isShowingSettings && onSectionToggle(sectionKey)}
            >
              <div className="section-card-header">
                <div className="section-title-area">
                  <h3>{sectionKey.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())}</h3>
                  {!isShowingSettings && (
                    <span className="category-badge">
                      {sectionData.category}
                    </span>
                  )}
                </div>
                {hasSettings && (
                  <button
                    className={`settings-wheel ${isShowingSettings ? 'active' : ''}`}
                    onClick={(e) => toggleSettingsView(sectionKey, e)}
                    title={isShowingSettings ? 'Show description' : 'Show settings'}
                  >
                    ⚙️
                  </button>
                )}
              </div>
              
              {isShowingSettings ? (
                <div className="section-settings">
                  {Object.entries(sectionData.settings).map(([settingKey, setting]) => (
                    <SettingInput
                      key={settingKey}
                      setting={setting}
                      value={sectionSettings?.[sectionKey]?.[settingKey]}
                      onChange={onSettingChange}
                      sectionKey={sectionKey}
                      settingKey={settingKey}
                    />
                  ))}
                </div>
              ) : (
                <p>{sectionData?.description || 'No description available'}</p>
              )}
            </div>
          );
        })
      }
      </div>
    </div>
  );
}

export default SectionsSelector;
